import { useCallback, useEffect, useState } from 'react'

import { SERVICE_ID_TO_INTEGRATION_TYPE_MAPPING, UserCreateUpdateIntegrations } from '../models/User'
import { selectAccountId } from '../store/auth/authSlice'
import { useAppSelector } from '../store/hooks'
import { useLazyGetAdditionalObjectsQuery } from '../store/integrations/integrations'
import { IntegrationServiceId, isContactLinkingWithAdditionalObjects } from './integrations'

type RegisterAdditionalObjectsValidator = (validator: AdditionalObjectsValidator) => () => void

export interface UseAdditionalObjectsContactLinkingParams {
  serviceId: IntegrationServiceId
  initialExternalId?: string
  registerAdditionalObjectsValidator?: RegisterAdditionalObjectsValidator
}

export interface UseAdditionalObjectsContactLinkingResult {
  isAdditionalObjectsContactLinking: boolean
  identifierValue: string
  identifierError: string | null
  isLoadingIdentifier: boolean
  handleIdentifierChange: (value: string) => void
}

const INVALID_IDENTIFIER_MESSAGE = 'Invalid external identifier. Please check and try again.'

export type AdditionalObjectsValidationResult = {
  isValid: boolean
  integrations: UserCreateUpdateIntegrations[]
}

export type AdditionalObjectsValidator = (integrations: UserCreateUpdateIntegrations[]) => Promise<AdditionalObjectsValidationResult>

export type AdditionalObjectsValidatorRegistry = {
  register: (validator: AdditionalObjectsValidator) => () => void
  run: (initialIntegrations: UserCreateUpdateIntegrations[]) => Promise<AdditionalObjectsValidationResult>
}

export const createAdditionalObjectsValidatorRegistry = (): AdditionalObjectsValidatorRegistry => {
  const validators: AdditionalObjectsValidator[] = []

  const register = (validator: AdditionalObjectsValidator): (() => void) => {
    validators.push(validator)

    return () => {
      const index = validators.indexOf(validator)
      if (index !== -1) {
        validators.splice(index, 1)
      }
    }
  }

  const run = async (initialIntegrations: UserCreateUpdateIntegrations[]): Promise<AdditionalObjectsValidationResult> => {
    if (!validators.length) {
      return {
        isValid: true,
        integrations: initialIntegrations,
      }
    }

    const initialResult: AdditionalObjectsValidationResult = {
      isValid: true,
      integrations: initialIntegrations,
    }

    const finalResult = await validators.reduce<Promise<AdditionalObjectsValidationResult>>(async (previousPromise, validator) => {
      const previous = await previousPromise
      const current = await validator(previous.integrations)

      return {
        isValid: previous.isValid && current.isValid,
        integrations: current.integrations,
      }
    }, Promise.resolve(initialResult))

    return finalResult
  }

  return {
    register,
    run,
  }
}

interface WoveInvestors {
  INVESTOR?: unknown[] | unknown
}

interface WoveAccountHoldingsResult {
  INVESTORS?: WoveInvestors
  TOTAL_INVESTORS?: number
}

interface WoveAccountHoldings {
  GET_INVESTOR_ACCT_HOLDINGS_RESULT?: WoveAccountHoldingsResult
}

const hasAdditionalObjectsData = (serviceId: IntegrationServiceId, additionalObjects: unknown): boolean => {
  if (!additionalObjects || typeof additionalObjects !== 'object') {
    return false
  }

  const data = additionalObjects as Record<string, unknown>

  if (serviceId === IntegrationServiceId.WOVE) {
    const accountHoldings = data['investor-account-holdings'] as WoveAccountHoldings | undefined
    const result = accountHoldings?.GET_INVESTOR_ACCT_HOLDINGS_RESULT
    const investors = result?.INVESTORS?.INVESTOR
    const totalInvestors = result?.TOTAL_INVESTORS

    if (!investors && (totalInvestors === undefined || totalInvestors === 0)) {
      return false
    }

    if (Array.isArray(investors)) {
      return investors.length > 0
    }

    return !!investors
  }

  if (Array.isArray(additionalObjects)) {
    return additionalObjects.length > 0
  }

  return Object.values(data).some((value) => {
    if (Array.isArray(value)) {
      return value.length > 0
    }

    if (value && typeof value === 'object') {
      return Object.values(value as Record<string, unknown>).some((inner) => {
        if (Array.isArray(inner)) {
          return inner.length > 0
        }
        return !!inner && typeof inner === 'object'
      })
    }

    return !!value
  })
}

export const useAdditionalObjectsContactLinking = (params: UseAdditionalObjectsContactLinkingParams): UseAdditionalObjectsContactLinkingResult => {
  const { serviceId, initialExternalId, registerAdditionalObjectsValidator } = params

  const accountId = useAppSelector(selectAccountId)

  const isAdditionalObjectsContactLinking = isContactLinkingWithAdditionalObjects(serviceId)

  const [identifierValue, setIdentifierValue] = useState<string>(initialExternalId ?? '')
  const [identifierError, setIdentifierError] = useState<string | null>(null)

  const [triggerGetAdditionalObjects, { isFetching: isFetchingIdentifier }] = useLazyGetAdditionalObjectsQuery()

  const handleIdentifierChange = useCallback((value: string) => {
    setIdentifierValue(value)
    setIdentifierError(null)
  }, [])

  const validateIntegrations = useCallback(
    async (integrations: UserCreateUpdateIntegrations[]): Promise<AdditionalObjectsValidationResult> => {
      if (!isAdditionalObjectsContactLinking) {
        return {
          isValid: true,
          integrations,
        }
      }

      const integrationIndex = integrations.findIndex((integration) => integration.serviceId === serviceId)

      if (!identifierValue) {
        setIdentifierError(null)
        setIdentifierValue('')

        if (integrationIndex === -1) {
          return {
            isValid: true,
            integrations,
          }
        }

        const updatedIntegrations = integrations.filter((_, index) => index !== integrationIndex)

        return {
          isValid: true,
          integrations: updatedIntegrations,
        }
      }

      try {
        const additionalObjects = await triggerGetAdditionalObjects({
          accountId,
          connector: serviceId,
          relatedTo: { id: identifierValue, type: 'contact' },
        }).unwrap()

        if (!hasAdditionalObjectsData(serviceId, additionalObjects as unknown)) {
          setIdentifierError(INVALID_IDENTIFIER_MESSAGE)
          return {
            isValid: false,
            integrations,
          }
        }

        const baseIntegration = integrationIndex > -1 ? integrations[integrationIndex] : undefined

        const integrationType = baseIntegration?.type || SERVICE_ID_TO_INTEGRATION_TYPE_MAPPING[serviceId] || 'crm'

        const updatedIntegration: UserCreateUpdateIntegrations = {
          ...(baseIntegration || {}),
          serviceId,
          externalId: identifierValue,
          externalAccountId: baseIntegration?.externalAccountId ?? identifierValue,
          type: integrationType,
        }

        const updatedIntegrations = [...integrations]
        if (integrationIndex > -1) {
          updatedIntegrations[integrationIndex] = updatedIntegration
        } else {
          updatedIntegrations.push(updatedIntegration)
        }

        setIdentifierError(null)
        return {
          isValid: true,
          integrations: updatedIntegrations,
        }
      } catch {
        setIdentifierError(INVALID_IDENTIFIER_MESSAGE)
        return {
          isValid: false,
          integrations,
        }
      }
    },
    [accountId, identifierValue, isAdditionalObjectsContactLinking, serviceId, triggerGetAdditionalObjects],
  )

  useEffect(() => {
    if (!isAdditionalObjectsContactLinking || !registerAdditionalObjectsValidator) {
      return
    }

    const unregister = registerAdditionalObjectsValidator(validateIntegrations)

    return unregister
  }, [isAdditionalObjectsContactLinking, registerAdditionalObjectsValidator, validateIntegrations])

  return {
    isAdditionalObjectsContactLinking,
    identifierValue,
    identifierError,
    isLoadingIdentifier: isFetchingIdentifier,
    handleIdentifierChange,
  }
}
